import * as React from "react";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  ButtonColor,
  ButtonVariant,
  CloseIcon,
  IconButton,
  IconButtonEdge,
  InputMode,
  Modal,
  Spinner,
  SpinnerColor,
  Text,
  TextVariant,
  useBrandColors,
} from "../../ui";
import { useIsTouchDevice } from "../../../hooks/useIsTouchDevice";
import { VehicleInput, VehicleType } from "../../../generated/nest-graphql";
import { getMakesByYear, getModelsByYearAndMake, getYears } from "../../../lib/quoteFormUtil";
import { US_STATES } from "../../PreJobChecklistV2/constants";
import {
  ParkingBrake,
  RearDrumBrakes,
  EditVehicleErrors,
  EditVehicleValues,
  toFormValues,
  toVehicleInput,
  validate,
} from "./editVehicleForm.utils";
import { Field } from "./Field";
import { Option, fieldId, valueOf } from "./editVehicleField.utils";
import { Full } from "./Full";
import { ReadOnlyMileage } from "./ReadOnlyMileage";
import { Section } from "./Section";
import { TouchContext } from "./touchContext";

// Screen order, so Save can focus the highest field with an error.
const FIELD_ORDER: Array<keyof EditVehicleValues> = [
  "year",
  "subModel",
  "make",
  "model",
  "vin",
  "licensePlate",
  "licensePlateState",
  "odometer",
  "rearDrumBrakes",
  "parkingBrake",
  "wheelTorque",
  "frontRotorsDiscardThicknessInches",
  "rearRotorsDiscardThicknessInches",
  "oilCapacityQuarts",
  "CCA",
  "notes",
];

// "Not set" is the picker's own empty choice (RearDrumBrakes.NOT_SET).
const REAR_DRUM_BRAKES_OPTIONS: Array<[RearDrumBrakes, string]> = [
  [RearDrumBrakes.YES, "Yes"],
  [RearDrumBrakes.NO, "No"],
];

/** A list with the record's own value kept in it, so an unlisted value is shown rather than silently dropped. */
const withCurrent = (options: string[], current: string) =>
  current && !options.includes(current) ? [current, ...options] : options;

/**
 * Edits the vehicle record: grouped cards like the history's and Cancel / Save pinned at the bottom. On a phone it
 * is full screen with the system pickers and number pads; with a mouse, a centred window with searchable lists.
 * Save sends every field back (see toVehicleInput).
 */
export const EditVehicleDialog: React.FC<{
  open: boolean;
  vehicle: VehicleType;
  /** The record alone, without the job's values filled in; what came from the job is checked like an edit. */
  stored?: VehicleType | null;
  /** Where the record's mileage came from, as Specs shows it. */
  mileageSource?: string | null;
  onCancel: () => void;
  /** Rejects when the save fails; the form then stays open with the edits and says so. */
  onSave: (input: VehicleInput) => Promise<void>;
}> = ({ open, vehicle, stored, mileageSource, onCancel, onSave }) => {
  const { pageBackground } = useBrandColors();
  // A coarse pointer is a finger: phones and tablets get system pickers and a full-screen form.
  const touch = useIsTouchDevice();
  // The record as it was when the form opened: a re-read while editing (a Carfax check, the job page's own fetch)
  // must not reset what the technician is typing.
  const [opened, setOpened] = useState<VehicleType>(vehicle);
  const [openedStored, setOpenedStored] = useState<VehicleType>(stored ?? vehicle);
  const initial = useMemo(() => toFormValues(opened), [opened]);
  const baseline = useMemo(() => toFormValues(openedStored), [openedStored]);
  const [values, setValues] = useState<EditVehicleValues>(initial);
  const [attempted, setAttempted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const wasOpen = useRef(false);

  // Before paint, so a reopened form never shows the last session's values for a frame.
  useLayoutEffect(() => {
    if (open && !wasOpen.current) {
      setOpened(vehicle);
      setOpenedStored(stored ?? vehicle);
      setValues(toFormValues(vehicle));
      setAttempted(false);
      setSaveFailed(false);
    }
    wasOpen.current = open;
  }, [open, vehicle, stored]);

  const set = (field: keyof EditVehicleValues) => (value: string) =>
    setValues((current) => ({ ...current, [field]: value }));
  const setYear = (year: string) => {
    // A new year can change which makes and models exist, so they are picked again.
    setValues((current) => ({ ...current, year, make: year === current.year ? current.make : "", model: "" }));
  };
  const setMake = (make: string) => {
    setValues((current) => ({ ...current, make, model: make === current.make ? current.model : "" }));
  };

  const errors: EditVehicleErrors = validate(values, baseline);
  const hasErrors = Object.keys(errors).length > 0;
  const changed = JSON.stringify(values) !== JSON.stringify(initial);
  // A field's error shows once it differs from the record (edited, or filled in from the job), or once Save was tapped.
  const shown = (field: keyof EditVehicleValues) =>
    attempted || values[field] !== baseline[field] ? errors[field] : undefined;

  const save = async () => {
    if (hasErrors) {
      // Say why nothing happened: show every error and bring the first into view.
      setAttempted(true);
      const first = FIELD_ORDER.find((field) => errors[field]);
      const input = first && document.getElementById(fieldId(first));
      input?.scrollIntoView?.({ block: "center" });
      input?.focus();
      return;
    }
    setSaving(true);
    setSaveFailed(false);
    try {
      await onSave(toVehicleInput(values, opened));
    } catch {
      setSaveFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const years = getYears();
  const yearMakes = values.year ? getMakesByYear(values.year) : [];
  const makes = withCurrent(yearMakes, values.make);
  // Records typed elsewhere may differ in case ("TOYOTA"), which must not hide the make's models.
  const listedMake = yearMakes.find((make) => make.toLowerCase() === values.make.toLowerCase()) ?? values.make;
  const models = withCurrent(
    values.year && values.make ? getModelsByYearAndMake(values.year, listedMake) : [],
    values.model
  );
  // Saved as the name, like ops and the inspection; shown with the code so either can be searched.
  const states: Option[] = US_STATES.map((state): Option => [state.value, `${state.label} · ${state.value}`]);
  // A stored code (the fleet portal saves "TX") is kept as it is, not rewritten by an unrelated save.
  if (values.licensePlateState && !states.some((state) => valueOf(state) === values.licensePlateState)) {
    const named = US_STATES.find((state) => state.label === values.licensePlateState);
    states.unshift(named ? [values.licensePlateState, `${named.label} (as saved)`] : values.licensePlateState);
  }

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onCancel}
      // Full screen on a phone; a centred window with its own scroll on a desktop.
      fullScreen={touch}
      fullWidth
      aria-labelledby="edit-vehicle-title"
      surfaceStyle={{
        backgroundColor: pageBackground,
        ...(touch ? {} : { height: "min(90vh, 900px)", borderRadius: 8 }),
      }}
    >
      <TouchContext.Provider value={touch}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between gap-2 px-4 py-2 bg-[#1f2b3a] border-b border-[#44576e]">
            <Text id="edit-vehicle-title" variant={TextVariant.H6} style={{ fontSize: 18, fontWeight: 700 }}>
              Edit vehicle
            </Text>
            <IconButton
              aria-label="Close without saving"
              onClick={onCancel}
              disabled={saving}
              edge={IconButtonEdge.END}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="flex-1 overflow-y-auto flex flex-col gap-3 p-4">
            <Section title="Vehicle">
              <Field
                field="year"
                label="Year"
                value={values.year}
                onChange={setYear}
                options={withCurrent(years, values.year)}
                error={shown("year")}
                required
              />
              <Field
                field="subModel"
                label="Trim"
                value={values.subModel}
                onChange={set("subModel")}
                placeholder="e.g. LE"
              />
              {/* Full rows: makes and models can have long names. */}
              <Full>
                <Field
                  field="make"
                  label="Make"
                  value={values.make}
                  onChange={setMake}
                  options={makes}
                  error={shown("make")}
                  required
                />
              </Full>
              <Full>
                <Field
                  field="model"
                  label="Model"
                  value={values.model}
                  onChange={set("model")}
                  options={models}
                  error={shown("model")}
                  required
                />
              </Full>
            </Section>

            <Section title="Identification">
              <Full>
                <Field field="vin" label="VIN" value={values.vin} onChange={set("vin")} error={shown("vin")} />
              </Full>
              <Field
                field="licensePlate"
                label="License plate"
                value={values.licensePlate}
                onChange={set("licensePlate")}
              />
              <Field
                field="licensePlateState"
                label="State"
                value={values.licensePlateState}
                onChange={set("licensePlateState")}
                options={states}
              />
            </Section>

            <Section title="Brakes & specs">
              <Full>
                {/* Only an inspection sets the mileage; Ops correct it. */}
                <ReadOnlyMileage odometer={openedStored.odometer} source={mileageSource} />
              </Full>
              <Full>
                <Field
                  field="rearDrumBrakes"
                  label="Rear drum brakes"
                  value={values.rearDrumBrakes}
                  onChange={set("rearDrumBrakes")}
                  options={REAR_DRUM_BRAKES_OPTIONS}
                  placeholder="Not set"
                />
              </Full>
              <Field
                field="parkingBrake"
                label="Parking brake"
                value={values.parkingBrake}
                onChange={set("parkingBrake")}
                options={withCurrent(Object.values(ParkingBrake), values.parkingBrake)}
              />
              <Field
                field="wheelTorque"
                label="Wheel torque (ft-lb)"
                value={values.wheelTorque}
                onChange={set("wheelTorque")}
                inputMode={InputMode.DECIMAL}
              />
              <Field
                field="frontRotorsDiscardThicknessInches"
                label="Front discard (in)"
                value={values.frontRotorsDiscardThicknessInches}
                onChange={set("frontRotorsDiscardThicknessInches")}
                inputMode={InputMode.DECIMAL}
                error={shown("frontRotorsDiscardThicknessInches")}
              />
              <Field
                field="rearRotorsDiscardThicknessInches"
                label="Rear discard (in)"
                value={values.rearRotorsDiscardThicknessInches}
                onChange={set("rearRotorsDiscardThicknessInches")}
                inputMode={InputMode.DECIMAL}
                error={shown("rearRotorsDiscardThicknessInches")}
              />
              <Field
                field="oilCapacityQuarts"
                label="Oil capacity (qt)"
                value={values.oilCapacityQuarts}
                onChange={set("oilCapacityQuarts")}
                inputMode={InputMode.DECIMAL}
                error={shown("oilCapacityQuarts")}
              />
              <Field
                field="CCA"
                label="Battery CCA"
                value={values.CCA}
                onChange={set("CCA")}
                inputMode={InputMode.NUMERIC}
                error={shown("CCA")}
              />
            </Section>

            <Section title="Notes">
              <Full>
                <Field field="notes" label="Notes" value={values.notes} onChange={set("notes")} multiline />
              </Full>
            </Section>
          </div>

          <div className="flex flex-col gap-2 px-4 py-3 bg-[#1f2b3a] border-t border-[#44576e]">
            {saveFailed && (
              <Text role="alert" variant={TextVariant.BODY2} className="text-red-300">
                The vehicle could not be saved. Your changes are kept; try again in a moment.
              </Text>
            )}
            {attempted && hasErrors && (
              <Text role="alert" variant={TextVariant.BODY2} className="text-red-300">
                Fix the highlighted fields to save.
              </Text>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={ButtonVariant.OUTLINED}
                onClick={onCancel}
                disabled={saving}
                style={{ minHeight: 48, textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                variant={ButtonVariant.CONTAINED}
                color={ButtonColor.PRIMARY}
                onClick={save}
                disabled={saving || !changed}
                startIcon={saving ? <Spinner size={18} color={SpinnerColor.INHERIT} aria-hidden /> : undefined}
                style={{ minHeight: 48, textTransform: "none", fontWeight: 600 }}
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </TouchContext.Provider>
    </Modal>
  );
};
